import type {
  StaffImportJobDetail,
  StaffImportJobPage,
  StaffImportJobStatus,
  StaffImportRowPage,
} from "@lafayette/schemas";
import { useDataProvider } from "@refinedev/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { IMPORTS_DATA_PROVIDER, type ImportCommandMeta, importResources } from "./data-provider";

// Reads and the one generic call. Every call goes through the Refine data provider named `imports` and
// is cached by TanStack Query (the query client Refine owns). A command writes the job detail it gets
// back into the same cache entry, so the page shows the new version without a second request.

const IMPORT_LIST_PAGE_SIZE = 25;
/** The review shows the first 30 rows; "Load more" pages after that. */
const IMPORT_ROW_PAGE_SIZE = 30;

export const importKeys = {
  all: ["imports"] as const,
  lists: () => ["imports", "list"] as const,
  list: (status: StaffImportJobStatus | null) => ["imports", "list", status] as const,
  job: (jobId: string) => ["imports", "job", jobId] as const,
  rows: (jobId: string) => ["imports", "rows", jobId] as const,
};

/** Runs one call of the data provider's `custom` and returns its data. */
export function useImportRequest() {
  const getDataProvider = useDataProvider();
  return useCallback(
    async <T>(meta: ImportCommandMeta, payload: unknown): Promise<T> => {
      const response = await getDataProvider(IMPORTS_DATA_PROVIDER).custom?.({
        url: `${importResources.jobs}/${meta.command}`,
        method: "post",
        payload,
        meta,
      });
      return response?.data as T;
    },
    [getDataProvider],
  );
}

export function useImportJobFetcher() {
  const getDataProvider = useDataProvider();
  return useCallback(
    async (jobId: string, signal?: AbortSignal): Promise<StaffImportJobDetail> => {
      const response = await getDataProvider(IMPORTS_DATA_PROVIDER).getOne({
        resource: importResources.jobs,
        id: jobId,
        ...(signal === undefined ? {} : { meta: { signal } }),
      });
      return response.data as StaffImportJobDetail;
    },
    [getDataProvider],
  );
}

export function useImportJob(jobId: string | undefined) {
  const fetchJob = useImportJobFetcher();
  return useQuery({
    queryKey: importKeys.job(jobId ?? ""),
    queryFn: ({ signal }) => fetchJob(jobId as string, signal),
    enabled: jobId !== undefined,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The jobs, newest first, one keyset page at a time. */
export function useImportJobList(status: StaffImportJobStatus | null, enabled = true) {
  const getDataProvider = useDataProvider();
  return useInfiniteQuery({
    queryKey: importKeys.list(status),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }): Promise<StaffImportJobPage> => {
      const response = await getDataProvider(IMPORTS_DATA_PROVIDER).getList({
        resource: importResources.jobs,
        meta: {
          ...(status === null ? {} : { status }),
          cursor: pageParam,
          limit: IMPORT_LIST_PAGE_SIZE,
          signal,
        },
      });
      return {
        items: response.data as StaffImportJobPage["items"],
        nextCursor: (response as unknown as { nextCursor: string | null }).nextCursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The rows of a saved draft, 30 at a time (R3). Each page passes the last row number of the page before. */
export function useImportRows(jobId: string | undefined) {
  const request = useImportRequest();
  return useInfiniteQuery({
    queryKey: importKeys.rows(jobId ?? ""),
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }): Promise<StaffImportRowPage> =>
      request<StaffImportRowPage>(
        { command: "listRows", jobId: jobId as string, signal },
        { limit: IMPORT_ROW_PAGE_SIZE, afterRowNumber: pageParam },
      ),
    getNextPageParam: (lastPage) => lastPage.nextAfterRowNumber,
    enabled: jobId !== undefined,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
